import React, { useEffect, useState } from 'react';
import * as RRD from 'react-router-dom';
import { connect } from 'react-redux';
import { Spinner } from 'reactstrap';
import { useTranslation, Trans } from 'react-i18next';
import 'dayjs/locale/sv';
import * as Sentry from '@sentry/react';

import { ApplicationState } from 'store/redux';
import { IAuthenticationActionCreators, LoginState, UserState } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import Context from 'configuration/context';
import {
  authenticationServiceFactory,
  IAuthenticationService,
} from 'services/authenticationService';
import AFSFiles, { IAFSFile } from 'components/controls/AFSFiles';
import './Afs.css';

export type IAfsProps = IAuthenticationActionCreators & {
  UserState: UserState;
};

const Afs: React.FunctionComponent<React.PropsWithChildren<IAfsProps>> = ({
  UserState,
  setLoginState,
}: React.PropsWithChildren<IAfsProps>) => {
  const { t } = useTranslation('translation');
  const [ongoing, setOngoingAfsFiles] = useState([] as IAFSFile[]);
  const [upcoming, setUpcomingAfsFiles] = useState([] as IAFSFile[]);
  const [inEffect, setInEffectAfsFiles] = useState([] as IAFSFile[]);
  const [isLoading, setIsLoading] = useState(false);

  const navigateTo = RRD.useNavigate();

  const location = RRD.useLocation();
  const { pathname } = location;

  function PathStartsWith(actualPathTokens: string[], possibleMatchPathTokens: string[]) {
    if (possibleMatchPathTokens.length > actualPathTokens.length) {
      return false;
    }
    for (var i = 0; i < possibleMatchPathTokens.length; i++) {
      if (possibleMatchPathTokens[i] !== actualPathTokens[i]) {
        return false;
      }
    }
    return true;
  }

  const splitLocation = pathname.split('/').filter((s) => s !== '');

  let isActiveRoute = (possibleMatch: string, exactMatch: boolean) => {
    if (pathname === possibleMatch) {
      return true;
    }
    return exactMatch
      ? false
      : PathStartsWith(
          splitLocation,
          possibleMatch.split('/').filter((s) => s !== '')
        );
  };

  const ongoingFilter: string = 'Pågående föreskriftsarbete';
  const upcomingFilter: string = 'Beslutade föreskrifter som ej trätt i kraft';
  const inEffectFilter: string = 'Beslutade föreskrifter som trätt i kraft 12 mån tillbaka';

  const loginState = UserState?.LoginState;

  useEffect(() => {
    // Anything in here is fired on component mount.
    if (loginState && loginState === LoginState.LOGGED_IN) {
      setIsLoading(true);
      const authenticationService = authenticationServiceFactory(
        Context.APIUrl,
        { setLoginState },
        UserState
      );

      fetchAfsDocuments(authenticationService)
        .then((files: IAFSFile[]) => {
          setOngoingAfsFiles(files.filter((i) => i.category === ongoingFilter));
          setUpcomingAfsFiles(files.filter((i) => i.category === upcomingFilter));
          setInEffectAfsFiles(files.filter((i) => i.category === inEffectFilter));
          setIsLoading(false);
        })
        .catch((error: any) => {
          Sentry.captureException(error);
          setIsLoading(false);
        });
    }
  }, [loginState, UserState]);

  var fetchAfsDocuments = (authenticationService: IAuthenticationService): Promise<IAFSFile[]> => {
    return new Promise<IAFSFile[]>((resolve, reject) => {
      authenticationService.getOrRenewNativeToken().then((msAccessToken: string) => {
        fetch(Context.APIUrl + '/v1/CustomerPortal/GetAFSDocuments', {
          method: 'POST',
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache',
          credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'bearer ' + msAccessToken,
          },
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        })
          .then((response) => {
            response.json().then((responseAsJson: IAFSFile[]) => {
              resolve(responseAsJson);
            });
          })
          .catch((error: any) => {
            console.warn('Could not fetch user details');
            reject(error);
          });
      });
    });
  };

  var fetchAfsDocument: (
    authenticationService: IAuthenticationService,
    url: string,
    filename: string
  ) => Promise<{}> = (
    authenticationService: IAuthenticationService,
    url: string,
    filename: string
  ): Promise<{}> => {
    return new Promise<{}>((resolve, reject) => {
      authenticationService.getOrRenewNativeToken().then((msAccessToken: string) => {
        fetch(url, {
          method: 'GET',
          mode: 'cors', // no-cors, *cors, same-origin
          cache: 'no-cache',
          credentials: 'same-origin', // include, *same-origin, omit
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'bearer ' + msAccessToken,
          },
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        })
          .then((response) => {
            response.blob().then((text: Blob) => {
              let file = new File([text], filename + '.pdf', {
                type: 'application/octet-stream',
              });
              let exportUrl = URL.createObjectURL(file);
              const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
              a.href = exportUrl;
              a.download = filename + '.pdf';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(exportUrl);
              resolve({});
            });
          })
          .catch((error: any) => {
            Sentry.captureException(error);
            console.warn('Could not fetch user details');
            reject(error);
          });
      });
    });
  };

  const onFileClick = (href: string, filename: string) => {
    if (loginState && loginState === LoginState.LOGGED_IN) {
      fetchAfsDocument(
        authenticationServiceFactory(Context.APIUrl, { setLoginState }, UserState),
        href,
        filename
      );
    }
  };

  var infoContent = <></>;
  var items: IAFSFile[] = [];

  if (isActiveRoute('/service/afs/ongoing', false)) {
    infoContent = <p>{t('afs.IN_PROGRESS_INFO')}</p>;
    items = ongoing;
  }

  if (isActiveRoute('/service/afs/upcoming', false)) {
    infoContent = <p>{t('afs.UPCOMING_INFO')}</p>;
    items = upcoming;
  }

  if (isActiveRoute('/service/afs/in-effect', false)) {
    infoContent = (
      <>
        <p>{t('afs.IN_EFFECT_INFO')}</p>
        <p>
          <Trans i18nKey="afs.AMV_LINK" ns="translation">
            Föreskrifter som trätt i kraft längre tillbaks än 12 månader hittar ni på{' '}
            <a href="https://www.av.se/arbetsmiljoarbete-och-inspektioner/publikationer/foreskrifter/">
              Arbetsmiljöverkets webbsida.
            </a>
          </Trans>
        </p>
      </>
    );
    items = inEffect;
  }

  return (
    <>
      <div className="row limited">
        <div className="col-12 image-container">
          <img src="/assets/images/hero-afs.jpg" alt="" />
        </div>
      </div>
      <div className="row mt-4 limited">
        <div className="col-12">
          <ul className="afs-navigation">
            <li
              className={isActiveRoute('/service/afs', true) ? 'active col' : 'col'}
              onClick={() => navigateTo('/service/afs')}
            >
              {t('afs.START')}
            </li>
            <li
              className={isActiveRoute('/service/afs/ongoing', false) ? 'active col' : 'col'}
              onClick={() => navigateTo('/service/afs/ongoing')}
            >
              {t('afs.IN_PROGRESS')}
            </li>
            <li
              className={isActiveRoute('/service/afs/upcoming', false) ? 'active col' : 'col'}
              onClick={() => navigateTo('/service/afs/upcoming')}
            >
              {t('afs.UPCOMING')}
            </li>
            <li
              className={isActiveRoute('/service/afs/in-effect', false) ? 'active col' : 'col'}
              onClick={() => navigateTo('/service/afs/in-effect')}
            >
              {t('afs.IN_EFFECT')}
            </li>
          </ul>
        </div>
      </div>
      <div className="row limited">
        <div className="col">
          <div className="afs-content">
            <div className="row">
              {isActiveRoute('/service/afs', true) ? (
                <div className="col">
                  <p className="ingress">{t('afs.START_INFO_GOAL')}</p>
                  <p>{t('afs.START_INFO')}</p>
                  <p>
                    <Trans i18nKey="afs.START_INFO_CONTACT" ns="translation">
                      Behöver du hjälp eller har synpunkter på denna funktion kan du kontakta oss på
                      mailadress <a href="mailto:info@avonova.se">info@avonova.se</a>
                    </Trans>
                  </p>
                </div>
              ) : (
                <>
                  <div className="col-md-6 col-12">{infoContent}</div>
                  {isLoading ? (
                    <h1 className="loading-indicator-container col-12">
                      <Spinner />
                      {t('afs.FETCHING_FILES')}...
                    </h1>
                  ) : (
                    <div className="col-12">
                      <AFSFiles onFileClick={onFileClick} items={items} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect((appState: ApplicationState) => ({ UserState: appState.authentication }), {
  ...AuthenticationStore.actions,
})(Afs);
